// Печатният документ „Инвентаризация": заглавие с номера, склада и кой я е
// въвел; банер „Анулиран на … · причина" за анулирана; таблицата (Търсено ·
// Намерено · Разлика · Статус · Ед. цена · Нето · Бруто) и реда „Нетна
// стойност"; футър Анулирай · Редактирай | PDF · Принтирай. Действията по
// документа се показват само когато е отворен от ред и е активен.
// Приготвените рецепти — над таблицата („Шопска салата × 3 — Домати 0,630 кг ·
// …", от снимката на документа), а в „Намерено" на продукта — „от тях 0,630 в
// рецепти" (намереното е насипно + в рецепти).
//
// `@page { size: A4; margin: 0 }` и двойката stocktake-doc-paper /
// printing-stocktake са НАРОЧНО различни от другите документни модали.
import { useEffect } from "react";
import { Modal } from "antd";
import { CloseOutlined, EditOutlined, PrinterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { modalStyles } from "../../../../utils/modalStyles";
import { useViewport } from "../../../../hooks/useViewport";
import { formatQty, useUnitDecimals } from "../../components/qty";
import { fmtDate } from "../../components/dateTime";
import { formatUnitPrice } from "../../components/numbers";
import {
  DANGER_TEXT_BUTTON_CLASS,
  PRIMARY_BUTTON_CLASS,
  SECONDARY_BUTTON_CLASS,
} from "../../components/list/buttonStyles";
import type { StocktakeDocument } from "../../../../api/inventoryStocktakes";
import { DocumentRevisionsPanel } from "../../components/revisions/DocumentRevisionsPanel";
import { formatSignedMoney, isStocktakeAnnulled } from "./stocktakeMeta";
import { formatPortionsInput } from "./stocktakeRecipes";

type Props = {
  open: boolean;
  onClose: () => void;
  stocktake: StocktakeDocument | null;
  onEdit?: () => void;
  onAnnul?: () => void;
  // „Печат" от реда в справката — отваря и веднага печата
  autoPrint?: boolean;
};

// Печат само на документа: изваждаме .stocktake-doc-paper в <body>, маркираме
// body с .printing-stocktake (CSS скрива всичко останало), печатаме и връщаме
// node-а на мястото му.
const printDocument = () => {
  const paper = document.querySelector(".stocktake-doc-paper") as HTMLElement | null;
  if (!paper || !paper.parentElement) {
    window.print();
    return;
  }
  const parent = paper.parentElement;
  const placeholder = document.createComment("stocktake-doc-placeholder");
  parent.insertBefore(placeholder, paper);
  document.body.appendChild(paper);
  document.body.classList.add("printing-stocktake");
  const restore = () => {
    document.body.classList.remove("printing-stocktake");
    if (placeholder.parentElement) parent.insertBefore(paper, placeholder);
    placeholder.remove();
    window.removeEventListener("afterprint", restore);
  };
  window.addEventListener("afterprint", restore);
  requestAnimationFrame(() => window.print());
};

// Тон на знакова стойност в таблицата (нулата наследява цвета)
const toneOf = (value: number) =>
  value > 0 ? "text-ok-deep" : value < 0 ? "text-danger-ink" : "";

export const StocktakeDocumentModal = ({
  open,
  onClose,
  stocktake,
  onEdit,
  onAnnul,
  autoPrint = false,
}: Props) => {
  const { t } = useTranslation();
  const decimalsOf = useUnitDecimals();
  const { isMobile, isTablet } = useViewport();

  useEffect(() => {
    if (!open || !autoPrint || !stocktake) return;
    const timer = setTimeout(printDocument, 250);
    return () => clearTimeout(timer);
  }, [open, autoPrint, stocktake]);

  if (!stocktake) return null;
  const annulled = isStocktakeAnnulled(stocktake);
  const recipes = stocktake.recipes ?? [];
  const width = isMobile ? undefined : isTablet ? 720 : 880;

  const statusLabel = (kind: string) =>
    kind === "substitute"
      ? t("inventoryPage.statusSubstitute")
      : kind === "surplus"
        ? t("inventoryPage.statusSurplus")
        : kind === "equal"
          ? t("inventoryPage.statusEqual")
          : t("inventoryPage.statusShortage");

  const subtitle = [
    t("inventoryPage.docStocktakeSubtitle", { id: stocktake.id }),
    stocktake.warehouseName || null,
    stocktake.createdByName
      ? t("inventoryPage.enteredBy", { name: stocktake.createdByName })
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const info: { label: string; value: string }[] = [
    { label: t("inventoryPage.colDate"), value: fmtDate(stocktake.date) },
    { label: t("inventoryPage.fieldReason"), value: stocktake.reason || "—" },
    { label: t("inventoryPage.colWarehouse"), value: stocktake.warehouseName || "—" },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 20px)" }}
    >
      {/* Печатът (A4, само листът) и таблицата на документа — scoped CSS,
          защото @page/@media print и клетките на <table> са извън Tailwind */}
      <style>{`
        @page { size: A4; margin: 0; }
        @media print {
          body.printing-stocktake > *:not(.stocktake-doc-paper) { display: none !important; }
          body.printing-stocktake { background: #fff !important; }
          body.printing-stocktake .stocktake-doc-paper {
            display: block !important;
            position: static !important;
            box-shadow: none !important;
            padding: 14mm 12mm !important;
          }
        }
        .stocktake-doc table { width: 100%; border-collapse: collapse; }
        .stocktake-doc th, .stocktake-doc td {
          border: 1px solid #C9C5BC; padding: 6px 8px; font-size: 12px;
        }
        .stocktake-doc th {
          background: #F0EDE6; text-align: left; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.4px; font-size: 10px;
          color: #52525B;
        }
        .stocktake-doc td.num, .stocktake-doc th.num { text-align: right; font-variant-numeric: tabular-nums; }
        .stocktake-doc td.ctr, .stocktake-doc th.ctr { text-align: center; }
        .stocktake-doc .annulled-stamp { color: #b42318; font-weight: 700; letter-spacing: 0.1em; }
      `}</style>

      <div className="flex max-h-[90vh] flex-col bg-surface">
        {/* Заглавие (не се печата) */}
        <div
          className={`flex items-start gap-3 border-b border-shade-2 ${
            isMobile ? "px-4 py-3.5" : "px-[22px] py-[18px]"
          }`}
        >
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="text-lg font-semibold leading-[1.2] text-ink">
              {t("inventoryPage.docStocktakeTitle")}
            </span>
            <span className="text-[13px] leading-[normal] text-body">{subtitle}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("inventoryPage.close")}
            className="flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-muted hover:text-ink"
          >
            <CloseOutlined className="text-sm" />
          </button>
        </div>

        {annulled && (
          <div
            className={`rounded-[9px] bg-fill-2 px-3.5 py-2.5 text-[13.5px] leading-[1.45] text-ink-2 ${
              isMobile ? "mx-4 mt-3.5" : "mx-[22px] mt-3.5"
            }`}
          >
            {t("inventoryPage.annulledBanner", {
              date: stocktake.deletedAt
                ? dayjs(stocktake.deletedAt).format("DD.MM.YYYY HH:mm")
                : "—",
            })}
            {stocktake.deletedByName ? ` · ${stocktake.deletedByName}` : ""}
            {stocktake.deleteReason ? ` · ${stocktake.deleteReason}` : ""}
          </div>
        )}

        {/* Документ (печата се само това) */}
        <div className={`min-h-0 overflow-y-auto ${isMobile ? "p-4" : "px-[22px] py-4"}`}>
          {/* История на промените — над листа, не се печата */}
          <DocumentRevisionsPanel key={stocktake.id} group="stocktakes" movementId={stocktake.id} />
          <div className="stocktake-doc stocktake-doc-paper bg-surface text-ink">
            <h2 className="mb-3 mt-0 flex items-center gap-3 text-lg font-extrabold leading-[normal] text-ink">
              {t("inventoryPage.docStocktakeHeading")}
              {annulled && (
                <span className="annulled-stamp text-sm">
                  {t("inventoryPage.statusAnnulledStamp")}
                </span>
              )}
            </h2>
            <div className={`mb-4 grid gap-2.5 ${isMobile ? "grid-cols-1" : "grid-cols-3"}`}>
              {info.map((item) => (
                <div key={item.label}>
                  <div className="text-[9.5px] font-bold uppercase leading-[normal] tracking-[0.6px] text-muted">
                    {item.label}
                  </div>
                  <div className="mt-0.5 text-[13px] leading-[normal] text-ink">{item.value}</div>
                </div>
              ))}
            </div>

            {recipes.length > 0 && (
              <div className="mb-4">
                <div className="text-[9.5px] font-bold uppercase leading-[normal] tracking-[0.6px] text-muted">
                  {t("inventoryPage.sr6DocRecipes")}
                </div>
                {recipes.map((recipe) => (
                  <div key={recipe.recipeId} className="mt-1 text-[12.5px] leading-[1.4] text-ink">
                    <b>
                      {recipe.recipeName} × {formatPortionsInput(recipe.portions)}
                    </b>
                    <span className="text-body">
                      {" — "}
                      {recipe.ingredients
                        .map(
                          (ingredient) =>
                            `${ingredient.productName} ${formatQty(ingredient.quantity, decimalsOf(ingredient.productId))}${
                              ingredient.unit ? ` ${ingredient.unit}` : ""
                            }`,
                        )
                        .join(" · ")}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <table>
              <thead>
                <tr>
                  <th>{t("inventoryPage.colProductName")}</th>
                  <th className="num">{t("inventoryPage.colSystem")}</th>
                  <th className="num">{t("inventoryPage.colCounted")}</th>
                  <th className="num">{t("inventoryPage.colDiff")}</th>
                  <th className="ctr">{t("inventoryPage.colStatus")}</th>
                  <th className="num">{t("inventoryPage.colUnitPrice")}</th>
                  <th className="num">{t("inventoryPage.colNet")}</th>
                  <th className="num">{t("inventoryPage.colGross")}</th>
                </tr>
              </thead>
              <tbody>
                {stocktake.lines.map((line) => {
                  const decimals = decimalsOf(line.productId);
                  const tone = toneOf(line.diff);
                  return (
                    <tr key={line.id}>
                      <td>{line.productName}</td>
                      <td className="num">
                        {line.systemQty != null ? formatQty(line.systemQty, decimals) : "—"}
                      </td>
                      <td className="num">
                        {line.counted != null ? formatQty(line.counted, decimals) : "—"}
                        {/* частта в приготвените рецепти (намереното е насипно + тя) */}
                        {line.inRecipes ? (
                          <div className="text-[10.5px] font-normal text-muted">
                            {t("inventoryPage.sr6DocInRecipes", { qty: formatQty(line.inRecipes, decimals) })}
                          </div>
                        ) : null}
                      </td>
                      {/* Разликата: + зелено / − червено */}
                      <td className={`num font-bold ${tone}`}>
                        {line.diff > 0 ? "+" : ""}
                        {formatQty(line.diff, decimals)}
                      </td>
                      <td className="ctr">{statusLabel(line.kind)}</td>
                      {/* Ед. цена (нето, до 5 знака) — само за разликата (нова
                          партида / FIFO на липсата) */}
                      <td className="num">{line.diff !== 0 ? formatUnitPrice(line.unitPrice) : "—"}</td>
                      {/* Нето/Бруто = записаните снимки на СТОЙНОСТТА НА РАЗЛИКАТА */}
                      <td className={`num font-bold ${tone}`}>
                        {line.diff !== 0 ? formatSignedMoney(line.sumNoVat) : "—"}
                      </td>
                      <td className={`num font-bold ${tone}`}>
                        {line.diff !== 0 ? formatSignedMoney(line.sumWithVat) : "—"}
                      </td>
                    </tr>
                  );
                })}
                {/* Тоталът НЕ сумира намереното (липса и излишък не се събират) —
                    показва знаковата НЕТИРАНА стойност на разликите (излишък −
                    липса) от снимките на записа (както в справката). */}
                <tr>
                  <td colSpan={6} className="font-bold">
                    {t("inventoryPage.colNetValue")}
                  </td>
                  <td className={`num font-extrabold ${toneOf(stocktake.totalValueNoVat)}`}>
                    {formatSignedMoney(stocktake.totalValueNoVat)}
                  </td>
                  <td className={`num font-extrabold ${toneOf(stocktake.totalValueWithVat)}`}>
                    {formatSignedMoney(stocktake.totalValueWithVat)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Футър (не се печата) */}
        <div
          className={`print:hidden flex flex-wrap items-center gap-2 border-t border-shade-2 ${
            isMobile ? "px-4 py-3" : "px-[22px] pb-[18px] pt-3.5"
          }`}
        >
          {!annulled && onAnnul && (
            <button type="button" onClick={onAnnul} className={DANGER_TEXT_BUTTON_CLASS}>
              {t("inventoryPage.docDelete")}
            </button>
          )}
          {!annulled && onEdit && (
            <button type="button" onClick={onEdit} className={SECONDARY_BUTTON_CLASS}>
              <EditOutlined />
              {t("inventoryPage.docEdit")}
            </button>
          )}
          <div className="flex-1" />
          <button
            type="button"
            onClick={printDocument}
            title={t("inventoryPage.pdfHint")}
            className={SECONDARY_BUTTON_CLASS}
          >
            PDF
          </button>
          <button type="button" onClick={printDocument} className={PRIMARY_BUTTON_CLASS}>
            <PrinterOutlined className="text-[15px]" />
            {t("inventoryPage.print")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default StocktakeDocumentModal;
